import { EntityType, Rights, sharedFromAccountName } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { TransactionNivalizExtendedClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedNivalizTransaction, TransactionState, TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { renderCopyableTrackingNumberCell } from '../../../components/object-list-cells/copyable-tracking-number-cell.component';
import {
    SHARED_COLUMN_HEADER,
    SHARED_COLUMN_HIDE,
    SHARED_COLUMN_ID,
    renderResolvedEntityCell,
    renderResolvedEntityListCell,
    renderSharedColumnCell
} from '../../../components/object-list-cells/object-list-cells.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { ApplicationCreateButton } from '../../components/create-button';
import OverviewHeader from '../../header/overview-header';
import { CreateTransactionModal } from '../../pudo/create-transaction-modal/create-transaction-modal.component';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { useNivalizTransactions } from './transactions.controller';

export const NivalizTransactions = () => {
    const viewProps = useNivalizTransactions();
    return (
        <div
            key='nivaliz-transactions'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.transactions'
                        description='The name of the transactions feature'
                        defaultMessage='Transactions'
                    />
                }
                createButton={
                    <ApplicationCreateButton
                        modalId='nivaliz-create-transaction'
                        renderDefaultModal={() => <CreateTransactionModal formId={viewProps.formId} />}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[600px]' }}
                        createPermissions={[{ item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}
                        entity={CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                        functionality={viewProps.functionality}
                        wizardId={viewProps.functionalityWizardId}
                        requiresWizard
                        label={
                            <FormattedMessage
                                id='feature.transactions.create-transaction'
                                defaultMessage='Create transaction'
                            />
                        }
                    />
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['all', 'scheduled', 'active', 'completed', 'failed']}
                    displayMap={
                        new Map([
                            [
                                'all',
                                <FormattedMessage
                                    id='feature.transactions.tabs.all'
                                    defaultMessage='All'
                                    key='feature.transactions.tabs.all'
                                />
                            ],
                            [
                                'scheduled',
                                <FormattedMessage
                                    id='feature.transactions.tabs.scheduled'
                                    defaultMessage='Scheduled'
                                    key='feature.transactions.tabs.scheduled'
                                />
                            ],
                            [
                                'active',
                                <FormattedMessage
                                    id='feature.transactions.tabs.active'
                                    defaultMessage='Active'
                                    key='feature.transactions.tabs.active'
                                />
                            ],
                            [
                                'completed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.completed'
                                    defaultMessage='Completed'
                                    key='feature.transactions.tabs.completed'
                                />
                            ],
                            [
                                'failed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.failed'
                                    defaultMessage='Failed'
                                    key='feature.transactions.tabs.failed'
                                />
                            ]
                        ])
                    }
                    defaultActive='all'
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <TransactionList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const TransactionList = (viewProps: ReturnType<typeof useNivalizTransactions>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'all':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='all'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            state: TransactionState.ALL_EXCEPT_DRAFT.map((state) => state.value),
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'state', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'state']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'receivers',
                            'deceased',
                            'type',
                            'state',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT],
                            ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'type',
                                (obj) => (
                                    <td
                                        key={obj.row.original.feature ?? 'type'}
                                        className={defaultCellClassName}>
                                        <DefaultBadge
                                            label={
                                                obj.row.original.feature === Feature.INBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.coffin'
                                                        description='Label for Nivaliz coffin (inbound) transaction type'
                                                        defaultMessage='Coffin'
                                                    />
                                                ) : obj.row.original.feature === Feature.OUTBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.urn'
                                                        description='Label for Nivaliz urn (outbound) transaction type'
                                                        defaultMessage='Urn'
                                                    />
                                                ) : (
                                                    '/'
                                                )
                                            }
                                        />
                                    </td>
                                )
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'receivers',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.receivers, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'state',
                                (obj) => (
                                    <td
                                        key={obj.row.original.state}
                                        className='text-foreground-default truncate p-2 text-sm font-medium'>
                                        <TransactionStateBadge state={obj.row.original.state} />
                                    </td>
                                )
                            ],
                            [
                                'spot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            entityType={EntityType.SPOT}
                                            display={obj.row.original.spot?.name}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            accountName={sharedFromAccountName(obj.row.original.shared)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'slot',
                                (obj) => (
                                    <td
                                        key={obj.row.original.slot?.entity_id}
                                        className={defaultCellClassName}>
                                        {obj.row.original.slot ? (
                                            <PlatformObjectBadge
                                                id={obj.row.original.slot?.entity_id}
                                                entityType={EntityType.SLOT}
                                                display={obj.row.original.slot?.name}
                                                key={obj.row.original.slot?.entity_id}
                                                parentIds={{
                                                    spot_id: obj.row.original.slot.data.spot_id,
                                                    module_id: obj.row.original.slot.data.module_id
                                                }}
                                                foreign={obj.row.original.slot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        ) : (
                                            <span>/</span>
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='all-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'scheduled':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='scheduled'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            state: [TransactionStateName.TRANSACTION_SCHEDULED],
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'receivers',
                            'deceased',
                            'type',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT],
                            ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'type',
                                (obj) => (
                                    <td
                                        key={obj.row.original.feature ?? 'type'}
                                        className={defaultCellClassName}>
                                        <DefaultBadge
                                            label={
                                                obj.row.original.feature === Feature.INBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.coffin'
                                                        description='Label for Nivaliz coffin (inbound) transaction type'
                                                        defaultMessage='Coffin'
                                                    />
                                                ) : obj.row.original.feature === Feature.OUTBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.urn'
                                                        description='Label for Nivaliz urn (outbound) transaction type'
                                                        defaultMessage='Urn'
                                                    />
                                                ) : (
                                                    '/'
                                                )
                                            }
                                        />
                                    </td>
                                )
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'receivers',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.receivers, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.state}
                                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='scheduled-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'active':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='active'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            state: [
                                TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                                TransactionStateName.TRANSACTION_PICK_UP_PENDING,
                                ...TransactionState.NOT_COMPLETED_CUSTOMER.map((state) => state.value)
                            ],
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp']}
                    columns={{
                        columns: [
                            SHARED_COLUMN_ID,
                            'tracking_number',
                            'senders',
                            'receivers',
                            'deceased',
                            'type',
                            'spot',
                            'slot',
                            'schedule_stop_timestamp',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            [
                                'schedule_stop_timestamp',
                                <FormattedMessage
                                    id='feature.nivaliz.transactions.expected-pick-up'
                                    description='Column label for expected pick-up date and time in Nivaliz transaction lists'
                                    defaultMessage='Expected pick-up'
                                />
                            ],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT],
                            ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'type',
                                (obj) => (
                                    <td
                                        key={obj.row.original.feature ?? 'type'}
                                        className={defaultCellClassName}>
                                        <DefaultBadge
                                            label={
                                                obj.row.original.feature === Feature.INBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.coffin'
                                                        description='Label for Nivaliz coffin (inbound) transaction type'
                                                        defaultMessage='Coffin'
                                                    />
                                                ) : obj.row.original.feature === Feature.OUTBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.urn'
                                                        description='Label for Nivaliz urn (outbound) transaction type'
                                                        defaultMessage='Urn'
                                                    />
                                                ) : (
                                                    '/'
                                                )
                                            }
                                        />
                                    </td>
                                )
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'receivers',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.receivers, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.state}
                                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='active-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'completed':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='completed'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            state: [
                                TransactionStateName.TRANSACTION_COMPLETED,
                                TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
                                TransactionStateName.TRANSACTION_CANCELLED
                            ],
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id', 'before', 'after'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                            ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number']}
                    columns={{
                        columns: [SHARED_COLUMN_ID, 'tracking_number', 'senders', 'receivers', 'deceased', 'type', 'spot', 'slot'],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'type',
                                (obj) => (
                                    <td
                                        key={obj.row.original.feature ?? 'type'}
                                        className={defaultCellClassName}>
                                        <DefaultBadge
                                            label={
                                                obj.row.original.feature === Feature.INBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.coffin'
                                                        description='Label for Nivaliz coffin (inbound) transaction type'
                                                        defaultMessage='Coffin'
                                                    />
                                                ) : obj.row.original.feature === Feature.OUTBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.urn'
                                                        description='Label for Nivaliz urn (outbound) transaction type'
                                                        defaultMessage='Urn'
                                                    />
                                                ) : (
                                                    '/'
                                                )
                                            }
                                        />
                                    </td>
                                )
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'receivers',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.receivers, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.state}
                                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='completed-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
        case 'failed':
            return (
                <ReactiveObjectList<ExtendedNivalizTransaction>
                    key='failed'
                    fetch={(arg) =>
                        TransactionNivalizExtendedClient.fetchExtendedNivalizTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.NIVALIZ,
                            state: TransactionStateName.TRANSACTION_FAILED,
                            // Nivaliz UI doesn't render collis/packages/PIN-codes.
                            without_collis: 'true',
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    hideColumns={SHARED_COLUMN_HIDE}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'deceased_name'],
                        filterKeys: ['spot_id', 'slot_id'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased_name', CommonMessage.OBJECTS.DECEASED.DEFAULT]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number']}
                    columns={{
                        columns: [SHARED_COLUMN_ID, 'tracking_number', 'senders', 'receivers', 'deceased', 'type', 'spot', 'slot'],
                        headerOverwrite: new Map([
                            [SHARED_COLUMN_ID, SHARED_COLUMN_HEADER],

                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['deceased', CommonMessage.OBJECTS.DECEASED.DEFAULT],
                            ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [SHARED_COLUMN_ID, (obj) => renderSharedColumnCell(obj.row.original.shared)],
                            [
                                'deceased',
                                (obj) =>
                                    renderResolvedEntityCell(obj.row.original.deceased, {
                                        entityTypeOverride: EntityType.DECEASED,
                                        parentShared: obj.row.original.shared,
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'type',
                                (obj) => (
                                    <td
                                        key={obj.row.original.feature ?? 'type'}
                                        className={defaultCellClassName}>
                                        <DefaultBadge
                                            label={
                                                obj.row.original.feature === Feature.INBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.coffin'
                                                        description='Label for Nivaliz coffin (inbound) transaction type'
                                                        defaultMessage='Coffin'
                                                    />
                                                ) : obj.row.original.feature === Feature.OUTBOUND_TRANSACTION ? (
                                                    <FormattedMessage
                                                        id='feature.nivaliz.transactions.type.urn'
                                                        description='Label for Nivaliz urn (outbound) transaction type'
                                                        defaultMessage='Urn'
                                                    />
                                                ) : (
                                                    '/'
                                                )
                                            }
                                        />
                                    </td>
                                )
                            ],
                            [
                                'senders',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.senders, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'receivers',
                                (obj) =>
                                    renderResolvedEntityListCell(obj.row.original.receivers, {
                                        entityTypeOverride: EntityType.USER,
                                        parentShared: obj.row.original.shared,
                                        className: 'text-brand-primary align-center truncate p-2 text-xs',
                                        emptyValue: <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    })
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                                accountName={sharedFromAccountName(obj.row.original.shared)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                    accountName={sharedFromAccountName(obj.row.original.shared)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='failed-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.NIVALIZ } })
                    }
                />
            );
    }
};
